import { abyssColors, white } from '@/theme/colors';
import { Theme } from '@emotion/react';
import { ArrowDropDown, InfoOutlined } from '@mui/icons-material';
import { CircularProgress, MenuItem, Stack, SxProps, Tooltip } from '@mui/material';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { ActionButton, StyledButtonMenu, tooltipProps } from './styles';

type Props = {
  onCalculate?: () => void;
  onUpdateComments?: () => void;
  disabled?: boolean;
  disableComments?: boolean;
  loading: boolean;
  loadingComments: boolean;
};

export type CalculateMenuRef = {
  handleClose: () => void;
};

export const CalculateMenu = forwardRef<CalculateMenuRef, Props>(
  (
    {
      disabled,
      disableComments = true,
      onCalculate = () => {},
      onUpdateComments = () => {},
      loading,
      loadingComments,
    },
    ref
  ) => {
    const [anchorElement, setAnchorElement] = useState<HTMLElement | undefined>(undefined);
    const [buttonStyle, setButtonStyle] = useState<SxProps<Theme>>({});
    const mainRef = useRef(null);

    const handleMenuClick = () => {
      setAnchorElement(mainRef.current ?? undefined);
      setButtonStyle({ backgroundColor: abyssColors.primary[700], color: white });
    };

    const handleClose = () => {
      setAnchorElement(undefined);
      setButtonStyle({});
    };

    useImperativeHandle(ref, () => ({
      handleClose,
    }));

    return (
      <>
        <Stack direction="row" gap="0.2rem">
          <ActionButton
            disableRipple
            contained
            sx={buttonStyle}
            disabled={disabled || loading}
            onClick={onCalculate}
          >
            {loading
              ? 'Calculating...'
              : loadingComments
                ? 'Updating...'
                : !loading && !loadingComments && 'Calculate & Update Table'}
            {(loading || loadingComments) && <CircularProgress size={20} />}
          </ActionButton>
          <ActionButton
            disableRipple
            ref={mainRef}
            contained
            onClick={handleMenuClick}
            disabled={disabled || loading}
            sx={{
              padding: '0.8rem 1rem',
              minWidth: 'fit-content',
            }}
          >
            <ArrowDropDown
              sx={{
                color: abyssColors.neutral[50],
                fontSize: '2.2rem',
                transform: anchorElement ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s ease-in-out',
              }}
            />
          </ActionButton>
        </Stack>
        <StyledButtonMenu
          elevation={0}
          anchorEl={anchorElement}
          open={Boolean(anchorElement)}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem onClick={onCalculate}>Calculate & Update Table</MenuItem>
          <MenuItem onClick={onUpdateComments} disabled={disableComments}>
            Update Comments
            <span
              style={{
                display: 'inline-flex',
                pointerEvents: 'auto',
              }}
              onClick={(e) => disableComments && e.stopPropagation()}
            >
              <Tooltip
                slotProps={tooltipProps}
                arrow
                title="This feature was developed based on a specific client request"
              >
                <InfoOutlined sx={{ fontSize: '1.4rem', color: abyssColors.primary[300] }} />
              </Tooltip>
            </span>
          </MenuItem>
        </StyledButtonMenu>
      </>
    );
  }
);
